import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import FieldLabel from "components/common/FieldLabel";
import {
  useCreateAccessGroupMutation,
  useGetReachPreviewQuery,
  useGetServiceTeamsQuery,
  useUpdateAccessGroupMutation,
} from "features/accessGroups/accessGroupsApi";
import type {
  AccessCatalog,
  AccessGroup,
  ClientScopeKey,
  DemoClientsKey,
  SurfaceKey,
  TierKey,
} from "types/interfaces/access";
import AccessGroupPermissionsPanel from "./AccessGroupPermissionsPanel";
import TierGuideDialog from "./TierGuideDialog";
import TierMatrix from "./TierMatrix";
import { appliesTo, withTier, type TierMap } from "./permissionsPanelModel";

// New groups sort after the seeded ones in the user picker.
const NEW_GROUP_SORT_ORDER = 100;

const SCOPE_HINTS: Record<ClientScopeKey, string> = {
  team: "Reads the Service Teams on each client record. No list to maintain here.",
  all: "Members see every client on the pages above.",
  assigned: "Members see the clients they are named on, and no others.",
};

const DEMO_OPTIONS: { value: DemoClientsKey; label: string; hint: string }[] = [
  {
    value: "exclude",
    label: "Exclude",
    hint:
      "Demo clients never appear for this group. This is the default, so " +
      "sample data stays out of everyone's lists without anyone configuring it.",
  },
  {
    value: "include",
    label: "Include",
    hint: "Demo clients appear like any other client the rule above takes in.",
  },
  {
    value: "only",
    label: "Only",
    hint: "Members see demo clients only - those the rule above takes in.",
  },
];

interface Props {
  open: boolean;
  catalog: AccessCatalog;
  group: AccessGroup | null;
  onClose: () => void;
  onSaved: (group: AccessGroup) => void;
}

function errorText(error: unknown): string {
  const data = (error as { data?: unknown })?.data;
  if (!data || typeof data !== "object") return "Could not save the group.";
  return Object.entries(data as Record<string, unknown>)
    .map(([field, value]) => {
      const text =
        typeof value === "string"
          ? value
          : Array.isArray(value)
            ? value.join(" ")
            : value && typeof value === "object"
              ? Object.entries(value as Record<string, unknown>)
                  .map(([k, v]) => `${k}: ${String(v)}`)
                  .join("; ")
              : String(value);
      return field === "error" || field === "non_field_errors"
        ? text
        : `${field}: ${text}`;
    })
    .join("\n");
}

/** A small uppercase badge: what kind of group this is, or that a value is
 * fixed. */
function Badge({
  label,
  color = "default",
}: {
  label: string;
  color?: "default" | "warning" | "success";
}) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={label}
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        borderRadius: 1,
      }}
    />
  );
}

/** A value a system group keeps, shown where a custom group has a field. */
function FixedValue({ value, hint }: { value: string; hint?: string }) {
  return (
    <Box
      sx={{
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 1,
        bgcolor: "action.hover",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2">{value}</Typography>
        <Badge label="FIXED" />
      </Box>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export default function AccessGroupDialog({
  open,
  catalog,
  group,
  onClose,
  onSaved,
}: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [subHeader, setSubHeader] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [tiers, setTiers] = useState<TierMap>({});
  const [team, setTeam] = useState<string | null>(null);
  const [scope, setScope] = useState<ClientScopeKey>("assigned");
  const [demo, setDemo] = useState<DemoClientsKey>("exclude");
  const [error, setError] = useState<string | null>(null);
  const { data: teamsAccess } = useGetServiceTeamsQuery(undefined, {
    skip: !open,
  });
  const teams = teamsAccess?.teams ?? [];
  const [createGroup, createState] = useCreateAccessGroupMutation();
  const [updateGroup, updateState] = useUpdateAccessGroupMutation();
  const saving = createState.isLoading || updateState.isLoading;
  const superAdmin = group?.slug === "super_admin";
  // Other screens rely on a system group's name, team and client rule.
  const system = Boolean(group?.is_system);

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? "");
    setDescription(group?.description ?? "");
    setSubHeader(group?.sub_header ?? "");
    setTiers({ ...(group?.tiers ?? {}) });
    setTeam(group?.team ?? null);
    setScope(group?.client_scope ?? "assigned");
    setDemo(group?.demo_clients ?? "exclude");
    setError(null);
  }, [open, group]);

  const teamName =
    teams.find((t) => t.slug === team)?.name ?? group?.team_name ?? null;
  const missingTeam = scope === "team" && !team;
  const scopeText = appliesTo(scope, teamName, demo);
  const { data: reach, isFetching: reachLoading } = useGetReachPreviewQuery(
    { client_scope: scope, team, demo_clients: demo },
    { skip: !open || missingTeam },
  );

  const setTier = (surface: SurfaceKey, tier: TierKey) =>
    setTiers((prev) => withTier(catalog, prev, surface, tier));

  const chooseTeam = (next: string | null) => {
    setTeam(next);
    // A team's clients need a team.
    if (!next && scope === "team") setScope("assigned");
  };

  const handleSave = async () => {
    setError(null);
    const body = {
      name: name.trim(),
      description,
      sub_header: subHeader,
      ...(superAdmin
        ? {}
        : {
            tiers,
            demo_clients: demo,
            ...(system ? {} : { team, client_scope: scope }),
          }),
    };
    try {
      const saved = group
        ? await updateGroup({ id: group.id, ...body }).unwrap()
        : await createGroup({
            ...body,
            sort_order: NEW_GROUP_SORT_ORDER,
          }).unwrap();
      onSaved(saved);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const scopeLabel = (value: ClientScopeKey) =>
    value === "team"
      ? `Every client serviced by ${teamName ?? "the group's team"}`
      : value === "all"
        ? "Every client"
        : teamName
          ? `Only the clients assigned to the person, serviced by ${teamName}`
          : "Only the clients assigned to the person";

  const reachLine = (() => {
    if (missingTeam) return "Pick a team to see which clients this reaches.";
    if (!reach || reachLoading) return null;
    if (reach.count === null)
      return `${reach.applies_to}: the clients differ from person to person.`;
    return null;
  })();

  return (
    <AppDialog
      open={open}
      onClose={saving ? () => undefined : onClose}
      title={group ? `Edit ${group.name}` : "New Internal Access Group"}
      maxWidth="lg"
      fullScreenBelow="md"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose} disabled={saving}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleSave}
            disabled={saving || !name.trim() || missingTeam}
          >
            {group ? "Save" : "Create Group"}
          </CustomButton>
        </>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 3fr) minmax(0, 2fr)",
          },
          gap: 3,
          pt: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {error && (
            <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ pt: 0.25 }}>
              {system ? (
                <Badge label="SYSTEM GROUP" color="warning" />
              ) : (
                <Badge label="CUSTOM GROUP" color="success" />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {system
                ? "Seeded with the platform. Its name, team and client rule are " +
                  "fixed because other screens rely on them. Page access is " +
                  "yours to set."
                : "Everything on this screen is editable."}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              <FieldLabel required={!system} htmlFor="access-group-name">
                Name
              </FieldLabel>
              {system ? (
                <FixedValue value={name} />
              ) : (
                <TextField
                  id="access-group-name"
                  size="small"
                  fullWidth
                  value={name}
                  placeholder="Group name"
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Box>
            <Box>
              <FieldLabel htmlFor="access-group-team">Team</FieldLabel>
              {system ? (
                <FixedValue
                  value={teamName ?? "No team"}
                  hint="Client owners show it; the client rule below reads it."
                />
              ) : (
                <>
                  <TextField
                    id="access-group-team"
                    select
                    size="small"
                    fullWidth
                    value={team ?? ""}
                    SelectProps={{ displayEmpty: true }}
                    disabled={saving}
                    onChange={(e) => chooseTeam(e.target.value || null)}
                  >
                    <MenuItem value="">No team</MenuItem>
                    {/* The group's team while the list is still loading. */}
                    {team && !teams.some((t) => t.slug === team) && (
                      <MenuItem value={team}>{teamName ?? team}</MenuItem>
                    )}
                    {teams.map((t) => (
                      <MenuItem key={t.slug} value={t.slug}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="caption" color="text.secondary">
                    The team members work in. Client owners show it; "Every
                    client serviced by" reads it.
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Box>
            <FieldLabel htmlFor="access-group-description">
              Who it is for
            </FieldLabel>
            <TextField
              id="access-group-description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={description}
              placeholder="The people this group is meant for"
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>
          <Box>
            <FieldLabel htmlFor="access-group-subheader">
              Sub-header shown in the user form
            </FieldLabel>
            <TextField
              id="access-group-subheader"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={subHeader}
              placeholder="A short line under the group's name, e.g. HR Services oversight"
              onChange={(e) => setSubHeader(e.target.value)}
            />
          </Box>
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
            >
              <FieldLabel sx={{ mb: 0 }}>Access per page</FieldLabel>
              <Tooltip title="What each tier allows">
                <IconButton
                  size="small"
                  aria-label="What each tier allows"
                  onClick={() => setGuideOpen(true)}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {superAdmin
                ? "Super Admin always holds every page."
                : system
                  ? `Editable on a system group - you can tune what ${name} ` +
                    "holds without changing what it is."
                  : "Set the tier this group holds on each page. A dash means " +
                    "the page doesn't define that tier."}
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflowX: "auto",
              }}
            >
              <TierMatrix
                catalog={catalog}
                value={tiers}
                onChange={setTier}
                disabled={superAdmin || saving}
              />
            </Box>
          </Box>
          <Box>
            <FieldLabel>Clients</FieldLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Which clients the pages above show. The tiers say what members may
              do; this says for which clients.
            </Typography>
            <RadioGroup
              value={scope}
              onChange={(e) => setScope(e.target.value as ClientScopeKey)}
            >
              {(["team", "all", "assigned"] as ClientScopeKey[]).map(
                (value) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    disabled={saving || system || (value === "team" && !team)}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          {scopeLabel(value)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {value === "team" && !team
                            ? "Pick the group's team first."
                            : value === "assigned" && teamName
                              ? "Members see the clients they are named on " +
                                `while ${teamName} works them, and no others.`
                              : SCOPE_HINTS[value]}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      alignItems: "flex-start",
                      mb: 0.5,
                      ml: 0,
                      mr: 0,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        scope === value ? "primary.main" : "transparent",
                      bgcolor:
                        scope === value ? "action.selected" : "transparent",
                    }}
                  />
                ),
              )}
            </RadioGroup>
            {system && (
              <Typography variant="caption" color="text.secondary">
                {teamName && scope === "team"
                  ? `Fixed: ${name} is the ${teamName} team, so its client ` +
                    "rule is what the name promises. A custom group can use " +
                    "any of the three."
                  : `Fixed: other screens rely on ${name}'s client rule. A ` +
                    "custom group can use any of the three."}
              </Typography>
            )}
          </Box>
          <Box>
            <FieldLabel>Demo clients</FieldLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Demo is set on the client record and gated here. It is not a
              service team, so it gets its own control.
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={superAdmin ? "include" : demo}
              onChange={(_, next: DemoClientsKey | null) => {
                if (next) setDemo(next);
              }}
              disabled={superAdmin || saving}
              aria-label="Demo clients"
            >
              {DEMO_OPTIONS.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    "&.Mui-selected, &.Mui-selected:hover": {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    },
                  }}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
              sx={{ mt: 0.5 }}
            >
              {superAdmin
                ? "Super Admin reaches every client, demo clients included."
                : DEMO_OPTIONS.find((option) => option.value === demo)?.hint}
            </Typography>
          </Box>
          <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
            {reach && reach.count !== null && !reachLoading && !missingTeam ? (
              <>
                Reaches{" "}
                <strong>
                  {reach.count} {reach.count === 1 ? "client" : "clients"}
                </strong>
                : {reach.applies_to}.
              </>
            ) : (
              (reachLine ?? `${scopeText}.`)
            )}
          </Alert>
        </Box>
        <Box>
          <AccessGroupPermissionsPanel
            catalog={catalog}
            tiers={tiers}
            subHeader="What members of this group can see and do."
            appliesTo={scopeText}
          />
        </Box>
      </Box>
      <TierGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        catalog={catalog}
      />
    </AppDialog>
  );
}
